import { ReactElement, useMemo } from 'react'
import { useUserActiveContactQueue } from '../api'
import { AgGridReact } from 'ag-grid-react'
import { ICellRendererParams } from 'ag-grid-community'
import { useTranslation } from 'react-i18next'
import { ACTIVE_GOALS_COLUMN_I18N_MAPPING } from '../constants'
import { actionKey } from '../../ApplicationManagement/ContactGoalAdmin/constants'
import { ProgressBar } from '@calabriocloud/beacon-ui'
import { formatDate } from '../../Recordings/columns'

const CellContent = ({ params, column }: { params: ICellRendererParams, column: string }): ReactElement | null => {
  switch (column) {
    case 'status':
      return (
        <div className="h-full w-full flex items-center justify-center">
          <div className="w-full">
            <ProgressBar
              label={params?.value?.[0] || ''}
              value={params.value?.[1] || 0}
              barClassName="w-full"
            />
          </div>
        </div>
      )
    case 'dueDate':
      return params.value ? <div>{formatDate(params.value)}</div> : null
    default:
      return <div>{params.value}</div>
  }
}

export const UserActiveContactQueueGrid = (): ReactElement => {
  const { data } = useUserActiveContactQueue()
  const { t } = useTranslation()
  const { progress: activeGoals } = data?.[0] || {}
  const columns = ['goalId', 'name', 'goalActionName', 'status', 'dueDate', 'totalContacts', 'contactsRemaining']

  const columnDefs = useMemo(() => {
    const i18nifiedColumnData = columns
      .map(column => ({
        field: column,
        headerName: t(ACTIVE_GOALS_COLUMN_I18N_MAPPING[column] || column),
        cellRenderer: (params: ICellRendererParams) => <CellContent params={params} column={column} />,
      }))
    return i18nifiedColumnData
  }, [columns, t])

  const rowData = useMemo(() => {
    return activeGoals?.map(goal => ({
      goalId: goal.goalId,
      name: goal.name,
      status: [goal.contactsCompleted === goal.totalForComplete ? t('completed') : t('inProgress'), goal?.totalProgress || 0], // Status is an array with label and percentage
      dueDate: goal?.dueDate || undefined,
      contactsRemaining: (goal?.totalForComplete - goal?.contactsCompleted) || 0,
      totalContacts: goal?.totalForComplete || 0,
      goalActionName: t(actionKey[goal.goalActionId] || ''),
    }))
  }, [activeGoals])

  return (
    <div className="ag-theme-material w-full h-[60vh] text-sm text-gray-700" style={{ borderRadius: '8px', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={{
          flex: 1,
          minWidth: 150,
          resizable: false,
          sortable: true,
          lockPinned: true,
          suppressMenu: true,
          headerClass: 'text-sm text-gray-700 font-semibold bg-gray-50',
        }}
        // TODO: Use a spinner component for loading state
        loadingOverlayComponent={() => <div className="ag-overlay-loading-center" data-testid="contact-queue-status-grid-loading-overlay">{t('loading')}</div>}
        noRowsOverlayComponent={() => <div className="ag-overlay-no-rows-center" data-testid="contact-queue-status-grid-no-rows-overlay">{t('contactQueueGoalsNoGoalToShow')}</div>}
        animateRows={false}
        data-testid="user-active-contact-queuegrid-aggrid"
      />
    </div>
  )
}
